import Vue from 'vue';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { getCountryByHostname, updateBrowserUrl } from '@/helpers/country';

import {
  MUTATIONS as UI_MUTATIONS,
  Modals,
  Loaders,
  SetModalVisibilityArgs,
  BannerState,
  CountryProfile,
  HeaderOptions,
  HeaderFlags,
  FooterOptions,
  FooterMode,
  ALL_MODAL_STRINGS,
  ACTIONS as UI_ACTIONS
} from '@/store/ui';
import { CustomerProfile, IngredientMetadata, LastSelectedElement, Recipe } from '@/models';
import { BrowserProduct, getBrowserProduct } from '@/helpers/browser';
import { Logger } from '../logger/logger';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { ui } from '@/constants';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { CustomerService } from '../customer/customer.service';
import { AccountPageLoadEvent } from '../analytics/events';
import { PageLoadEventType } from '../analytics/platforms/adobe-analytics/models';
import { AnalyticsService } from '../analytics/analytics.service';

export interface UiService {
  setAppScrollability(scrollable: boolean): void;
  setAppBlur(blurred: boolean): void;
  setContentBlur(blurred: boolean): void;
  setCountryProfile(countryProfile: CountryProfile, shouldRedirect: boolean, queryParams?: { [key: string]: string }): void;
  setAppPepperVisibility(visible: boolean): void;
  setBagRewardsToastVisible(visible: boolean): void;

  getBrowserProduct(): BrowserProduct;
  setBannerState(bannerState: BannerState): void;
  trackClientWidth(width: number): void;
  isMobile(): boolean;
  isDesktop(): boolean;
  isAemHomepageEnabled(): boolean;

  incrementLoader(loader: keyof Loaders): void;
  decrementLoader(loader: keyof Loaders): void;

  pushHeaderState(options: HeaderOptions): void;
  popHeaderState(): void;

  pushFooterState(options: FooterOptions): void;
  popFooterState(): void;

  setPointsCelebrationVisibility(isVisible: boolean): void;

  showMakeItMyOwnModal(mealName: string, actionAfterClick: () => void): void;
  showUnavailableIngredientsModal(mealName: string, actionAfterClick: () => void): void;
  setShouldScrollHomeToHash(value: boolean): void;
  setIsAemHomepageEnabled(value: boolean): void;
  setRewardConfirmationVisibility(value: boolean): void;

  /**
   * @param args a visible flag or ModalState that includes visible flag as one of the args
   */
  setModalVisibility<TModal extends keyof Modals>(modal: TModal, visible: boolean, args?: Modals[TModal]['args']): void;

  focusDiv(targetSelector: string): void;
  focusFirstDiv(targetSelector: string): void;
  focusBack(): void;
  firstFocus(): void;
  nextFocus(className: any): void;
  setLastSelectedElement(one: string, two?: string): void;
  clearLastSelectedElement(): void;
  pushEventTargetAsLastSelectedElement(event: any): void;

  showOnboardingModal(existingLoyaltyUser: boolean, customerService: CustomerService, customer?: CustomerProfile): void;
  setToastVisibility(visible: boolean): void;
  setChurrosToastAlreadyDisplayed(displayed: boolean): void;
  setDietaryFilterHintShown(dietaryFilterHintShown: boolean): void;
  setSignInSignUpVisibilityExperienceA(visible: boolean): void;
  setSignInSignUpVisibilityExperienceB(visible: boolean): void;
  setMobileBuilderNavExperienceA(visible: boolean): void;
  setMobileBuilderNavExperienceB(visible: boolean): void;
  toggleAppScrollability(visible: boolean): void;
  setIngredientPanelVisibility(visible: boolean): void;
  setRecipe(ingredient: Recipe | null): void;
  setIngredients(ingredients: IngredientMetadata[]): void;
  setIngredient(ingredient: IngredientMetadata | null): void;
  setRecipeBannerExpanded(expanded: boolean): void;
  setSelectedRecipeVisible(visible: boolean): void;
  setSelectedRecipeDistance(distance: number): void;
  showVerifyMobileNumber(verifyMobileNumberSown: boolean): void;
  setHasFailed2SV(failed: boolean): void;
  setUpdatedMobileNumberOnLogin(updatedMobileNumber: boolean): void;
  scrolltToYPosition(element: HTMLElement, offset: number): void;
}

export class AppUiService implements UiService {
  headerStateHistory: HeaderOptions[] = [{ flags: HeaderFlags.All & ~HeaderFlags.ReturnToRoute & ~HeaderFlags.LogoVisibleXS }];
  footerStateHistory: FooterOptions[] = [{ mode: FooterMode.Normal }];
  modalCounter = 0;

  constructor(private store: Store<AppState>, private logger: Logger, private analytics: AnalyticsService) {}

  setAppScrollability(scrollable: boolean) {
    this.store.commit(UI_MUTATIONS.SET_APP_SCROLLABILITY, scrollable);
    this.toggleAppScrollability(!scrollable);
  }

  // prevents scrolling when modal is open
  toggleAppScrollability(visible: boolean): void {
    const body = document.getElementsByTagName('body')[0];
    if (visible) {
      this.modalCounter += 1;
    } else if (this.modalCounter > 0) {
      this.modalCounter -= 1;
    }

    if (body && this.modalCounter >= 1) {
      body.classList.add('not-scrollable');
    } else if (body) {
      body.classList.remove('not-scrollable');
    }
  }

  setBagRewardsToastVisible(visible: boolean) {
    this.store.dispatch(UI_ACTIONS.SET_REWARD_TOAST_VISIBILITY, visible);
  }
  /**
   * When passing 'false' as args, the args will be cleared out of the state so that
   * the next time the modal is opened, args won't have old state accidentally
   */
  setModalVisibility<TModal extends keyof Modals>(modal: TModal, visible: boolean, args?: Modals[TModal]['args']) {
    const updatedState: SetModalVisibilityArgs<TModal> = {
      modal,
      state: { visible, args: args || null },
    };
    this.store.commit(UI_MUTATIONS.SET_MODAL_VISIBILITY, updatedState);
  }

  closeAllOverlays() {
    this.store.commit(UI_MUTATIONS.SET_BAG_VISIBILITY, undefined);
    ALL_MODAL_STRINGS.forEach(modalString => this.setModalVisibility(modalString as keyof Modals, false));
  }

  setAppBlur(blurred: boolean) {
    this.store.commit(UI_MUTATIONS.SET_APP_BLURRED, blurred);
  }

  setContentBlur(blurred: boolean) {
    this.store.commit(UI_MUTATIONS.SET_CONTENT_BLURRED, blurred);
  }

  setCountryProfile(countryProfile: CountryProfile, shouldRedirect: boolean = true, queryParams?: { [key: string]: string }) {
    const countryProfileFromHostname = getCountryByHostname(window.location.hostname);
    if (shouldRedirect && countryProfileFromHostname && countryProfileFromHostname.countryCode.toLowerCase() != countryProfile.countryCode.toLowerCase()) {
      updateBrowserUrl(window.location, countryProfile.topLevelDomain, queryParams);
    }

    this.store.commit(UI_MUTATIONS.SET_COUNTRY_PROFILE, countryProfile);
  }

  setAppPepperVisibility(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_APP_PEPPER_VISIBILITY, visible);
  }

  getBrowserProduct(): BrowserProduct {
    return getBrowserProduct(window.navigator);
  }

  pushHeaderState(options: HeaderOptions): void {
    const currentState = this.getCurrentHeaderState();
    if (currentState.flags == options.flags && currentState.route == options.route && currentState.routeText == options.routeText) {
      return;
    }

    this.headerStateHistory.push(options);
    this.setHeaderState(options);
  }

  popHeaderState(): void {
    this.headerStateHistory.pop();
    if (this.headerStateHistory.length < 1) {
      this.logger.unreachable('HeaderState array should never be empty');
      return;
    }

    this.setHeaderState(this.getCurrentHeaderState());
  }

  pushFooterState(options: FooterOptions): void {
    const currentState = this.getCurrentFooterState();
    if (currentState.mode == options.mode) {
      return;
    }

    this.footerStateHistory.push(options);
    this.setFooterState(options);
  }

  popFooterState(): void {
    this.footerStateHistory.pop();
    if (this.footerStateHistory.length < 1) {
      this.logger.unreachable('FooterState array should never be empty');
      return;
    }

    this.setFooterState(this.getCurrentFooterState());
  }

  isMobile(): boolean {
    return this.store.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  isDesktop(): boolean {
    return this.store.state.ui.clientWidth >= ui.breakpoints.desktop;
  }

  isAemHomepageEnabled(): boolean {
    return this.store.state.ui.home.isAemHomepageEnabled;
  }

  trackClientWidth(width: number) {
    this.store.commit(UI_MUTATIONS.SET_CLIENT_WIDTH, width);
  }

  incrementLoader(loader: keyof Loaders) {
    this.store.commit(UI_MUTATIONS.INCREMENT_LOADER, loader);
  }

  decrementLoader(loader: keyof Loaders) {
    this.store.commit(UI_MUTATIONS.DECREMENT_LOADER, loader);
  }

  setBannerState(bannerState: BannerState) {
    this.store.commit(UI_MUTATIONS.SET_BANNER_STATE, bannerState);
  }

  setPointsCelebrationVisibility(isVisible: boolean): void {
    this.store.commit(UI_MUTATIONS.SET_CELEBRATION_POINTS_VISIBILITY, isVisible);
  }

  showMakeItMyOwnModal(mealName: string, actionAfterClick: () => void): void {
    this.setModalVisibility('basicError', true, {
      header: 'CHANGING IT UP?',
      content: `We picked these ingredients especially for the ${mealName}. If you make changes, it'll be your own thing.
                This meal may not be eligible for bonuses or promotions.`,
      options: [
        {
          text: 'Cancel',
          type: ButtonType.Secondary,
          onClick: () => {
            this.setModalVisibility('basicError', false);
          },
        },
        {
          text: 'Continue',
          type: ButtonType.Primary,
          onClick: () => {
            this.setModalVisibility('basicError', false);
            actionAfterClick();
          },
        },
      ],
    });
  }

  showUnavailableIngredientsModal(mealName: string, actionAfterClick: () => void): void {
    this.setModalVisibility('basicError', true, {
      header: 'OH DANG.',
      content: `Some ingredients are unavailable for the ${mealName}. 
                If you make it your own, you can choose different ingredients but it'll be your own thing. 
                This meal may not be eligible for bonuses or promotions.`,
      options: [
        {
          text: 'Okay',
          type: ButtonType.Secondary,
          onClick: () => {
            this.setModalVisibility('basicError', false);
          },
        },
        {
          text: 'Make It My Own',
          type: ButtonType.Primary,
          onClick: () => {
            this.setModalVisibility('basicError', false);
            actionAfterClick();
          },
        },
      ],
    });
  }

  setShouldScrollHomeToHash(value: boolean): void {
    this.store.commit(UI_MUTATIONS.SET_SHOULD_SCROLL_HOME_TO_HASH, value);
  }

  setIsAemHomepageEnabled(value: boolean): void {
    this.store.commit(UI_MUTATIONS.SET_IS_AEM_HOMEPAGE_ENABLED, value);
  }

  setRewardConfirmationVisibility(value: boolean): void {
    this.store.commit(UI_MUTATIONS.REWARD_CONFIRMATION_VISIBILITY, value);
  }

  // moved function fullmodalFirstFocus() ===> $services.ui.focusDiv('.fullModal')
  focusDiv(targetSelector: string): void {
    setTimeout(() => {
      const counter = document.querySelectorAll(targetSelector).length;
      if (counter <= 1) {
        const cfocus = document.querySelectorAll(targetSelector)[0];
        const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
        focusTarget.focus();
      } else {
        const cfocus = document.querySelectorAll(targetSelector)[1];
        const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
        focusTarget.focus();
      }
    });
  }

  focusFirstDiv(targetSelector: string): void {
    setTimeout(() => {
      const counter = document.querySelectorAll(targetSelector).length;
      if (counter <= 1) {
        const cfocus = document.querySelectorAll(targetSelector)[0];
        const selection = document.querySelector('[tabindex = "0"]') !== null;
        if (selection) {
          const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
          focusTarget.focus();
        }
      }
    });
  }

  focusBack() {
    const element: any = this.store.state.ui.lastSelectedElement;
    if (element) {
      if (element.two) {
        const focusElement = document.getElementsByClassName(element.two)[0] as HTMLElement;
        if (focusElement) {
          focusElement.focus();
          this.setLastSelectedElement(element.one);
        }
      } else {
        const focusElement = document.getElementsByClassName(element.one)[0] as HTMLElement;
        if (focusElement) {
          focusElement.focus();
          this.clearLastSelectedElement();
        }
      }
    }
  }

  firstFocus() {
    Vue.nextTick(() => {
      setTimeout(() => {
        const counter = document.querySelectorAll('.cmg-modal-container').length;
        if (counter <= 1) {
          const cfocus = document.querySelectorAll('.cmg-modal-container')[0];
          const selection = document.querySelector('[tabindex = "0"]') !== null;
          if (selection) {
            const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
            focusTarget.focus();
          }
        } else {
          const cfocus = document.querySelectorAll('.cmg-modal-container')[1];
          const selection = document.querySelector('[tabindex = "0"]') !== null;
          if (selection) {
            const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
            focusTarget.focus();
          }
        }
      });
    });
  }

  // moved function callFocus.setSession(event); ===> $services.ui.pushEventTargetAsLastSelectedElement(event)
  pushEventTargetAsLastSelectedElement(event: any) {
    if (event.target.className) {
      const classname = event.target.className.replace(' focus-visible', '');
      const lastSelectedElement = this.store.state.ui.lastSelectedElement;
      if (lastSelectedElement) {
        this.setLastSelectedElement(lastSelectedElement.one, classname);
      } else {
        this.setLastSelectedElement(classname);
      }
    }
  }

  nextFocus(className: any) {
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusNearby = document.getElementsByClassName(className)[0] as HTMLElement;
        if (focusNearby) {
          focusNearby.focus();
        }
      });
    });
  }

  setLastSelectedElement(one: string, two?: string) {
    const focusBackItems: LastSelectedElement = { one, two };
    this.store.commit(UI_MUTATIONS.SET_LAST_SELECTED_ELEMENT, focusBackItems);
  }

  clearLastSelectedElement() {
    this.store.commit(UI_MUTATIONS.SET_LAST_SELECTED_ELEMENT, null);
  }

  showOnboardingModal(existingLoyaltyUser: boolean, customerService: CustomerService, customer?: CustomerProfile) {
    this.setModalVisibility('loyaltyExplainer', true, {
      state: LoyaltyExplainerModalState.RewardsGeneralOnboarding,
      existingLoyaltyUser,
    });
    
    this.analytics.send(new AccountPageLoadEvent(PageLoadEventType.WelcomeToRewards));

    if (customer) {
      customer.hasSeenRewardsGenOnboarding = true;
      customerService.updateCustomer({ hasSeenRewardsGenOnboarding: true });
    }

    Vue.nextTick(() => this.focusDiv('.content-root'));
  }

  private getCurrentHeaderState(): HeaderOptions {
    return this.headerStateHistory[this.headerStateHistory.length - 1];
  }

  private setHeaderState(options: HeaderOptions) {
    this.store.commit(UI_MUTATIONS.SET_HEADER_OPTIONS, options);
    let header = document.getElementById('aem-xf-header');
    if( header ) {
      if( options.flags & HeaderFlags.LogoVisibleXS ) {
        header.querySelector('#aem-header-left > .image')?.classList.remove('hide-xs');
      } else {
        header.querySelector('#aem-header-left > .image')?.classList.add('hide-xs');
      }
      if( options.flags & HeaderFlags.MenuNav ) {
        header.querySelector('#aem-header-center')?.classList.remove('d-none');
      } else {
        header.querySelector('#aem-header-center')?.classList.add('d-none');
      }
    }
  }

  private getCurrentFooterState(): FooterOptions {
    return this.footerStateHistory[this.footerStateHistory.length - 1];
  }

  private setFooterState(options: FooterOptions) {
    this.store.commit(UI_MUTATIONS.SET_FOOTER_OPTIONS, options);

    if( options.mode === FooterMode.None) {
      document.getElementById('aem-xf-footer')?.classList.remove('footer-micro');
      document.getElementById('aem-xf-footer')?.classList.add('d-none', 'footer-none');
    } else if ( options.mode === FooterMode.Micro ) {
      document.getElementById('aem-xf-footer')?.classList.remove('d-none', 'footer-none');
      document.getElementById('aem-xf-footer')?.classList.add('footer-micro');
    } else {
      document.getElementById('aem-xf-footer')?.classList.remove('d-none', 'footer-none', 'footer-micro');
    }
  }

  setToastVisibility(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_TOAST_VISIBILITY, visible);
  }

  setChurrosToastAlreadyDisplayed(displayed: boolean) {
    this.store.commit(UI_MUTATIONS.SET_CHURROS_TOAST_ALREADY_DISPLAYED, displayed);
  }

  setDietaryFilterHintShown(dietaryFilterHintShown: boolean) {
    this.store.commit(UI_MUTATIONS.SET_DIETARY_FILTER_HINT_SHOWN, dietaryFilterHintShown);
  }

  setIngredientPanelVisibility(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_INGREDIENT_PANEL_VISIBILITY, visible);
    this.toggleAppScrollability(visible);
  }

  setRecipe(ingredient: Recipe | null) {
    this.store.commit(UI_MUTATIONS.SET_RECIPE, ingredient);
  }
  
  setIngredients(ingredients: IngredientMetadata[]) {
    this.store.commit(UI_MUTATIONS.SET_INGREDIENTS, ingredients);
  }

  setIngredient(ingredient: IngredientMetadata | null) {
    this.store.commit(UI_MUTATIONS.SET_INGREDIENT, ingredient);
  }

  setRecipeBannerExpanded(expanded: boolean) {
    this.store.commit(UI_MUTATIONS.SET_RECIPE_BANNER_EXPANDED, expanded);
  }

  setSelectedRecipeVisible(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_RECIPE_VISIBLE, visible);
  }
  setSelectedRecipeDistance(distance: number) {
    this.store.commit(UI_MUTATIONS.SET_RECIPE_DISTANCE, distance);
  }

  setSignInSignUpVisibilityExperienceA(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_SIGNIN_SIGNUP_VISIBILITY_EXP_A, visible);
  }

  setSignInSignUpVisibilityExperienceB(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_SIGNIN_SIGNUP_VISIBILITY_EXP_B, visible);
  }

  setMobileBuilderNavExperienceA(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_MOBILE_BUILDER_NAV_EXPERIENCE_A, visible);
  }

  setMobileBuilderNavExperienceB(visible: boolean) {
    this.store.commit(UI_MUTATIONS.SET_MOBILE_BUILDER_NAV_EXPERIENCE_B, visible);
  }

  setSessionExpiredBannerShown(sessionExpiredBannerShown: boolean): void {
    this.store.commit(UI_MUTATIONS.SET_SESSION_EXPIRED_BANNER, sessionExpiredBannerShown);
  }

  showVerifyMobileNumber(verifyMobileNumberSown: boolean): void {
    this.store.commit(UI_MUTATIONS.SET_SHOW_VERIFY_MOBILE_NUMBER, verifyMobileNumberSown);
  }

  setHasFailed2SV(failed: boolean) {
    this.store.commit(UI_MUTATIONS.SET_HAS_FAILED_2SV, failed);
  }

  setUpdatedMobileNumberOnLogin(updatedMobileNumber: boolean) {
    this.store.commit(UI_MUTATIONS.SET_UPDATED_MOBILE_NUMBER_ON_LOGIN, updatedMobileNumber);
  }

  scrolltToYPosition(element: HTMLElement, offset: number = 0) {
    let position = element.getBoundingClientRect().top;
    window.scrollTo({top: position - offset + window.scrollY, behavior: 'smooth'});
  }
}
